import * as THREE from "three";
import { BufferGeometry, Line, Line3, MathUtils, Ray, Vector3 } from "three";
import { generateUUID } from "three/src/math/MathUtils";
import { ArcDimension } from "@/Dimensions/ArcDimension";
import { LineDimension } from "@/Dimensions/LineDimension";
import { IndserObject, IndsertPosition } from "@/core/IndserObject";
import { MLayer } from "@/core/MLayer";
import {
  DBEntity,
  DbLayer,
  DbLine,
  DrawManager,
  FMathUtils,
  ModelManager,
  ViewManager,
} from "@/CadEngine";
import { BPMathUtils } from "@/utils/BPMathUtils";
import {
  DrawOperateState,
  IDrawOperation,
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
} from "./IDrawOperation";
import { AnchorPointAux } from "@/Dimensions/AnchorPointAuxs";
import { BPLinear } from "@/core/BPLinear";
import { OrthogonalAux } from "@/Dimensions/OrthogonalAux";
import {
  ActiveInputType,
  CircleArcInfo,
  CircleArcInfoUseFilleted,
  CommonColor,
  DrawOperateLineParams,
} from "@/core/Parameters";
import { DimensionGroup } from "@/core/DimensionGroup";
import { Context } from "@/core/context";
import { ULabel } from "@/ui/ULabel";

class DrawOperationArc implements IDrawOperation {
  private m_model: ModelManager;
  private m_layer: DbLayer;
  private m_auxLayer: MLayer;
  private m_material: THREE.LineBasicMaterial;
  private m_tempObject: THREE.Line | undefined;
  private points: THREE.Vector3[] = [];
  private m_curPoint: THREE.Vector3 = new THREE.Vector3();
  private m_locationObject: THREE.Line;
  private view: ViewManager;
  private m_dimension: DimensionGroup;
  private canDrawCallBack: (params: DrawOperationMovePointParams) => boolean;
  private arcGeometry: BufferGeometry;
  public get Points() {
    return this.points;
  }
  private m_draw: DrawManager;
  private m_context: Context;
  private m_activeInputType: ActiveInputType = ActiveInputType.None;
  private m_inputValue: string = "";
  private m_distanceInputValue: string = "";
  private m_angleInputValue: string = "";
  private m_arcOptions: CircleArcInfo = {
    center: new THREE.Vector3(),
    radius: 0,
    startAngle: 0,
    endAngle: 0,
    clockwise: true,
  };
  private m_color: number;
  private m_TextLabel: ULabel;
  private m_isShift: Boolean = false;
  constructor(
    options: DrawOperateLineParams,
    canDrawCallBack: (params: DrawOperationMovePointParams) => boolean
  ) {
    this.m_model = options.model;
    this.m_layer = options.layer;
    this.m_color = options.color;
    this.m_auxLayer = options.auxLayer;
    this.m_material = new THREE.LineBasicMaterial({
      color: new THREE.Color(options.layer.Color),
    });
    this.view = options.view;
    this.m_context = this.view.m_context;
    this.m_locationObject =
      options.view.m_context.ToolsManager.CreatedLocationLine();
    this.m_dimension = new DimensionGroup(options.view, this.m_auxLayer);
    this.canDrawCallBack = canDrawCallBack;
    this.m_dimension.Line.SetVaule("");
    this.m_dimension.Arc.SetVaule("");

    this.arcGeometry = new THREE.BufferGeometry();
    this.arcLine = new THREE.LineSegments(this.arcGeometry, this.m_material);
    this.m_auxLayer.add(this.arcLine);
    this.m_draw = this.m_model.m_context.DrawManager;
    this.m_TextLabel = new ULabel(
      this.m_context.ViewManager,
      this.m_context.Dom
    );
    this.m_TextLabel.Visibility = true;
  }

  SetDrawLineForDashed() {
    this.m_material =
      this.m_draw.m_context.MaterialManager.createDashedLineMaterial(
        new THREE.Color(this.m_color)
      );
  }

  onMouseMove(e: PointerEvent): void {}

  onKeyDown(e: KeyboardEvent): void {
    e.preventDefault();
    if (e.keyCode == 16) {
      this.m_isShift = true;
    }
    if (e.key == "Escape") {
      this.m_context.DrawManager.DrawFinish(DrawOperateState.complete);
    }
    if (!this.points.length) return;
    this.m_angleInputValue = this.m_dimension.Arc.Input.Dom.value;
    this.m_distanceInputValue = this.m_dimension.Line.Input.Dom.value;

    if (e.key === "Tab") {
      this.m_inputValue = "";
      if (this.m_activeInputType == ActiveInputType.None) {
        this.m_activeInputType = ActiveInputType.Distance;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #ffffff";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
      } else if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_activeInputType = ActiveInputType.Angle;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #101010";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #ffffff";
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_activeInputType = ActiveInputType.Distance;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #ffffff";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
      }
    }
    if (e.key === "Backspace") {
      if (this.m_inputValue.length > 0) {
        this.m_inputValue = this.m_inputValue.substring(
          0,
          this.m_inputValue.length - 1
        );
      }
      if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_dimension.Line.Input.SetVaule(this.m_inputValue);
        if (this.m_inputValue) {
          this.m_distanceInputValue = this.m_inputValue;
        }
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_dimension.Arc.Input.SetVaule(this.m_inputValue + " °");
        if (this.m_inputValue) {
          this.m_angleInputValue = this.m_inputValue;
        }
      }
    } else if ((e.key >= "0" && e.key <= "9") || e.key == ".") {
      if (this.m_activeInputType == ActiveInputType.None) return;
      if (this.m_inputValue?.includes(".") && e.key == ".") return;
      this.m_inputValue += e.key;
      if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_dimension.Line.Input.SetVaule(this.m_inputValue);
        this.m_distanceInputValue = this.m_inputValue;
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_dimension.Arc.Input.SetVaule(this.m_inputValue + " °");
        this.m_angleInputValue = this.m_inputValue;
      }
    } else if (e.key == "Enter") {
      let length = parseFloat(this.m_distanceInputValue) * 0.001;
      let angle = THREE.MathUtils.degToRad(parseFloat(this.m_angleInputValue));
      let dir = new THREE.Vector3(1, 0, 0);
      dir.applyAxisAngle(new THREE.Vector3(0, 0, 1), angle);
      dir.multiplyScalar(length);
      let newPos = this.points[this.points.length - 1].clone().add(dir);
      this.points.push(newPos);
      this.updatedArcData();
      this.updatedAuxLine();
      if (this.points.length == 3) {
        this.m_draw.DrawFinish(DrawOperateState.complete);
      }
      this.m_context.ViewManager.Render();
    }
  }
  onKeyUp(e: KeyboardEvent): void {
    if (e.keyCode == 16) {
      this.m_isShift = false;
    }
  }

  private get originPoint() {
    return this.points[this.points.length - 1];
  }

  private get CurDir() {
    return this.m_curPoint.clone().sub(this.originPoint).normalize();
  }
  OnZoom(camera: THREE.Camera) {
    if (this.points.length > 0) {
      this.m_dimension.Line.SetDimension(
        this.originPoint,
        this.m_curPoint,
        this.Scale
      );
      this.m_dimension.Arc.SetDimension(
        this.originPoint,
        new THREE.Vector3(1, 0, 0),
        this.CurDir,
        this.Scale
      );
    }
  }
  OnWheel(e: WheelEvent): void {}
  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {
    return;
    let tolSq = params.tol * params.tol;

    if (this.points.length > 0) {
      let start = new THREE.Vector3();
      let end = new THREE.Vector3();
      let target = new THREE.Vector3();
      let targetOnSegment = new THREE.Vector3();
      let point = this.points[this.points.length - 1];
      if (this.points.length > 1) {
        for (let index = 0; index < this.points.length - 1; index++) {
          start.copy(this.points[index]);
          end.copy(this.points[index + 1]);
          let distance = params.ray.distanceSqToSegment(
            start,
            end,
            target,
            targetOnSegment
          ); // target在射线上，targetOnSegment在线段上
          //  console.log(target.x,target.y,target.z+'          '+targetOnSegment.x,targetOnSegment.y,targetOnSegment.z)
          if (distance <= tolSq) {
            // let location = {x:Number(target.x.toFixed(3)), y:Number(target.y.toFixed(3)), z:Number(target.z.toFixed(3))};
            let location = {
              x: Number(targetOnSegment.x.toFixed(3)),
              y: Number(targetOnSegment.y.toFixed(3)),
              z: Number(targetOnSegment.z.toFixed(3)),
            };
            let center = new THREE.Vector3(
              (start.x + end.x) * 0.5,
              (start.y + end.y) * 0.5,
              (start.z + end.z) * 0.5
            );
            let d1 = start.distanceTo(targetOnSegment);
            let d2 = center.distanceTo(targetOnSegment);
            let d3 = end.distanceTo(targetOnSegment);
            let position = IndsertPosition.None;
            let min = Math.min(d1, d2, d3);
            let adjust = targetOnSegment.clone();
            if (min <= params.tol) {
              if (min == d1) {
                position = IndsertPosition.Start;
                adjust = start.clone();
              }
              if (min == d2) {
                position = IndsertPosition.Center;
                adjust = center;
              }
              if (min == d3) {
                position = IndsertPosition.End;
                adjust = end.clone();
              }
            }
            let insert = new IndserObject(location, distance, "", []);
            insert.adjust = adjust;
            insert.position = position;
            insert.geo = new THREE.Line3(start.clone(), end.clone());
            params.array.push(insert);
          }
        } // 以上先是用当前移动线检测是否和自己画的线段相交，不包括最后一个线段

        //夹角矫正
        {
          // 这段代码作用为用当前移动线段来和自己画的线的最后一段线检测是否相交
          // alert('yyyyyyyy')
          let direction = new THREE.Vector3();
          this.view.Camera.getWorldDirection(direction);
          let line = new THREE.Line3(
            this.points[this.points.length - 2].clone(),
            this.points[this.points.length - 1].clone()
          );
          let p0 = new THREE.Vector3();
          let p = params.ray.intersectPlane(new THREE.Plane(direction, 0), p0);
          let dir0 = line.end.clone().sub(line.start).normalize();
          let dir1 = p0.clone().sub(line.end).normalize();
          let rad = BPMathUtils.angleTo(dir0, dir1, direction);
          let angle = (rad * 180) / Math.PI;
          let orginLength = p0.distanceTo(line.end);
          let tolAngle = (((params.tol / orginLength) * 180) / Math.PI) * 2; // orginLength越小自动偏转幅度越大

          let offsetAngle = (angle + tolAngle) % 90;
          if (offsetAngle <= tolAngle * 2) {
            //五度之内视为垂直
            rad = rad - ((offsetAngle - tolAngle) * Math.PI) / 180;
            let p = dir0
              .applyAxisAngle(direction, rad)
              .multiplyScalar(p0.distanceTo(line.end));

            p = line.end.clone().add(dir0);
            let insert = new IndserObject(
              p,
              Math.sqrt(params.ray.distanceSqToPoint(p)),
              "",
              []
            );
            insert.adjust = p;
            insert.position = IndsertPosition.AuxOrthogonal;
            params.array.push(insert);
          }
        }
      }
      //正交矫正
      {
        // 这段代码作用为 当只有一个点的时候移动线段和 X轴的相交情况
        let direction = new THREE.Vector3();
        this.view.Camera.getWorldDirection(direction);
        let start = this.points[this.points.length - 1].clone();
        let p0 = new THREE.Vector3();
        params.ray.intersectPlane(new THREE.Plane(direction, 0), p0);
        let dir0 = new THREE.Vector3(1, 0, 0);
        let dir1 = p0.clone().sub(start).normalize();
        let rad = BPMathUtils.angleTo(dir0, dir1, direction);
        let angle = (rad * 180) / Math.PI;
        let orginLength = p0.distanceTo(start);
        let tolAngle = (((params.tol / orginLength) * 180) / Math.PI) * 2;
        let offsetAngle = (angle + tolAngle) % 90;

        if (offsetAngle <= tolAngle * 2) {
          //五度之内视为垂直
          rad = rad - ((offsetAngle - tolAngle) * Math.PI) / 180;
          let p = dir0
            .applyAxisAngle(direction, rad)
            .multiplyScalar(p0.distanceTo(start));
          p = start.clone().add(dir0);
          let insert = new IndserObject(
            p,
            Math.sqrt(params.ray.distanceSqToPoint(p)),
            "",
            []
          );
          insert.adjust = p;
          insert.position = IndsertPosition.AuxOrthogonal;
          // insert.geo = new THREE.Line3(start.clone(), end.clone());
          params.array.push(insert);
        }
      }
    }
    let ray = new THREE.Ray();

    params.array.forEach((item) => {
      if (item.position == "none") item.position = IndsertPosition.Insert;
      if (item.geo) {
        if (item.geo instanceof THREE.Line3) {
          let line = item.geo as THREE.Line3;
          let p0 = item.adjust ?? item.location;
          let v0 = line.start.clone().sub(line.end.clone());
          let dir0 = v0.clone().normalize();
          if (this.points.length > 0) {
            let point = this.points[this.points.length - 1];
            let v1 = new THREE.Vector3(
              p0.x - point.x,
              p0.y - point.y,
              p0.z - point.z
            );
            let offset = dir0.multiplyScalar(v1.dot(dir0));
            if (offset.lengthSq() <= params.tol * params.tol) {
              //正交矫正
              item.position = IndsertPosition.Vertical;
              item.adjust = new THREE.Vector3(
                p0.x - offset.x,
                p0.y - offset.y,
                p0.z - offset.z
              );
            }
          }
          ray.set(line.start, line.end.clone().sub(line.start).normalize()); // 这里开始检测移动线段是否和场景中两个或两个以上的交点处接触
          params.array.forEach((item1) => {
            if (item1.geo instanceof THREE.Line3) {
              let line1 = item1.geo as THREE.Line3;
              // console.log(params,line,line1,line.start.clone().distanceTo(line1.start.clone()),'!!!!!!!!!');
              if (
                line != line1 &&
                line.start.clone().distanceTo(line1.start.clone()) >
                  0.0000000001 &&
                line.end.clone().distanceTo(line1.end.clone()) >
                  0.00000000001 &&
                line.start.y != line1.end.y
              ) {
                let target = new THREE.Vector3();
                let target1 = new THREE.Vector3();
                let distance = ray.distanceSqToSegment(
                  line1.start.clone(),
                  line1.end.clone(),
                  target
                );
                if (distance < 0.00000000001) {
                  line.closestPointToPoint(target, true, target1); //
                  if (target.distanceTo(target1) < 0.00000001) {
                    // 说明这两条线交叉了，因为这个点同时在两根线上
                    item.position = IndsertPosition.Cross;
                    item.adjust = target;
                    item.distance = target.distanceTo(
                      new THREE.Vector3(
                        item.location.x,
                        item.location.y,
                        item.location.z
                      )
                    );
                  }
                }
              }
            }
          });
        }
      }
    });
  }
  OnMovePoint(params: DrawOperationMovePointParams): void {
    this.m_draw.m_context.Dom.style.cursor = "none";
    this.m_locationObject.position.copy(params.point);
    this.m_locationObject.visible = true;
    this.m_curPoint = params.point.clone();
    let TextLabelLocation = new THREE.Vector3();
    TextLabelLocation.x = this.m_curPoint.x + this.Scale * 35;
    TextLabelLocation.y = this.m_curPoint.y - this.Scale * 10;
    this.m_TextLabel.SetVaule(
      "长按Shift锁定正交",
      TextLabelLocation,
      new THREE.Vector3(1, 0, 0)
    );
    if (!this.points.length) return;
    if (
      (this.m_context.DrawManager.isEnableQuadrature || this.m_isShift) &&
      this.points.length
    ) {
      this.m_curPoint = this.m_context.ToolsManager.GetOrthogonalityPoint(
        this.m_curPoint,
        this.points[this.points.length - 1]
      );
    }
    this.updatedAuxLine();
    this.updatedArcData();
    this.m_activeInputType = ActiveInputType.None;
    this.m_dimension.Line.Input.Dom.style.border = "1px solid #101010";
    this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
    this.m_inputValue = "";
  }
  OnDownPoint(params: DrawOperationMovePointParams): void {
    if (!this.canDrawCallBack(params)) {
      return;
    }
    if (
      (this.m_context.DrawManager.isEnableQuadrature || this.m_isShift) &&
      params.position == "none"
    ) {
      this.points.push(this.m_curPoint);
    } else {
      this.points.push(params.point);
    }
    if (this.points.length == 1) {
      this.m_tempObject = this.m_draw.m_context.ToolsManager.CreatedAuxLine();
    }
    if (this.points.length == 3) {
      if (this.m_draw.isEnableQuadrature && params.position !== "none") {
        this.updatedAuxLine();
        this.updatedArcData();
      }
      this.m_draw.DrawFinish(DrawOperateState.complete);
    }
  }
  GetPoints(): Vector3[] {
    return this.points;
  }

  Finish(operate: DrawOperateState) {
    let arcPoints: number[] = [];
    if (this.arcLine) {
      if (this.arcLine.geometry.attributes.position) {
        arcPoints = Array.from(this.arcLine.geometry.attributes.position.array);
      } else {
        arcPoints = [];
      }
    }
    this.clear();
    if (this.points.length < 3) return [];
    if (operate == DrawOperateState.complete) {
      if (this.points.length == 3) {
        let key = `${this.m_layer.Id}_${this.m_color}`;
        let lines: DbLine[] = this.m_model.Lines[key]?.lines ?? [];
        let indexs: number[] = [];
        let returnLine: {
          center: { x: number; y: number; z: number };
          p1: { x: number; y: number; z: number };
          p2: { x: number; y: number; z: number };
          isAnticlockwise: boolean;
          arcLine: DbLine | undefined;
          id?: string;
        } = {
          center: {
            x: this.m_arcOptions.center.x,
            y: this.m_arcOptions.center.y,
            z: 0,
          },
          p1: {
            x: this.points[0].x,
            y: this.points[0].y,
            z: 0,
          },
          p2: {
            x: this.m_curPoint.x,
            y: this.m_curPoint.y,
            z: 0,
          },
          isAnticlockwise: !this.m_arcOptions.clockwise,
          arcLine: undefined,
        };

        arcPoints.forEach((x, i) => {
          if (i % 3 == 0) {
            indexs.push(i / 3);
          }
        });
        let line = new DbLine({
          id: generateUUID(),
          layerId: this.m_layer.Id,
          blockId: "",
          points: new Float32Array(arcPoints),
          indexes: new Int32Array(indexs),
          color: this.m_color,
        });
        let sphere = new THREE.Sphere();
        let box = new THREE.Box3();
        box.setFromArray(line.points);
        box.getBoundingSphere(sphere);
        line.block_path = generateUUID();
        line.sphere = sphere;
        line.box = box;
        line.extend_id = generateUUID();
        lines.push(line);
        if (this.m_model.Lines[key]) {
          this.m_model.Lines[key].lines = lines;
          this.view.m_context.ToolsManager.LinesToModel(
            lines,
            this.m_model.Lines[key].model
          );
        } else {
          this.m_model.Lines[key] = {
            canEdit: true,
            lines: lines,
            model: this.m_model.addLinesToModel(
              this.m_layer.Id,
              this.m_color,
              lines
            ),
          };
        }
        returnLine.id = line.id;
        returnLine.arcLine = line;
        return returnLine;
      }
    } else if (operate == DrawOperateState.cancel) {
      return [];
    }
    return [];
  }

  private get Scale() {
    return this.view.ScreenModelScale;
  }

  private updatedArcData() {
    if (this.points.length < 2) return;
    let ps = [...this.points, this.m_curPoint];
    let points = this.executeArcAlgorithm(ps[0], ps[1], ps[2]);
    if (points.length > 2) {
      this.updateArc(points);
    }
  }
  private updatedAuxLine() {
    let newPoints = [this.points[this.points.length - 1], this.m_curPoint];
    this.m_tempObject?.geometry.setFromPoints(newPoints);
    this.m_tempObject?.computeLineDistances();
    this.updateDimension();
  }
  private updateDimension() {
    this.m_dimension.Line.SetDimension(
      this.points[this.points.length - 1],
      this.m_curPoint,
      this.Scale
    );
    this.m_dimension.Arc.SetDimension(
      this.points[this.points.length - 1],
      new THREE.Vector3(1, 0, 0),
      this.CurDir,
      this.Scale
    );
  }
  private clear() {
    this.m_context.ToolsManager.ObjectsClear([
      this.m_locationObject,
      this.m_tempObject!,
      this.arcLine!,
    ]);
    this.m_dimension?.dispose();
    this.m_TextLabel?.dispose();
  }

  private arcLine: THREE.Line | undefined;

  public updateArc(points: THREE.Vector2[] | THREE.Vector3[]) {
    this.arcGeometry.setFromPoints(points);
    this.arcGeometry!.attributes.position.needsUpdate = true;
    this.arcGeometry.computeBoundingBox();
    this.arcGeometry.computeBoundingSphere();
  }

  private executeArcAlgorithm(
    dataA: THREE.Vector3,
    dataB: THREE.Vector3,
    dataC: THREE.Vector3
  ): THREE.Vector2[] | THREE.Vector3[] {
    let points: THREE.Vector2[] | THREE.Vector3[] = [dataA, dataC];
    let judge =
      (dataB.x - dataA.x) * (dataC.y - dataB.y) -
      (dataB.y - dataA.y) * (dataC.x - dataB.x); //判断是否共线
    let arcDire = 1; //判断逆圆和顺圆
    if (judge != 0) {
      if (judge > 0) {
        arcDire = 0;
      } else if (judge < 0) {
        arcDire = 1;
      }
      this.m_arcOptions.clockwise = !!arcDire;
      //计算圆心和半径
      let a = dataA.x - dataB.x;
      let b = dataA.y - dataB.y;
      let c = dataA.x - dataC.x;
      let d = dataA.y - dataC.y;
      let e =
        (dataA.x * dataA.x -
          dataB.x * dataB.x -
          (dataB.y * dataB.y - dataA.y * dataA.y)) /
        2;
      let f =
        (dataA.x * dataA.x -
          dataC.x * dataC.x -
          (dataC.y * dataC.y - dataA.y * dataA.y)) /
        2;
      // a*x0 + b*y0 = e
      // c*x0 + d*y0 = f
      // 克莱姆法则
      // 克莱姆法则指出，如果系数矩阵的行列式 D ≠ 0，则方程组有唯一解，且每个未知数 xᵢ 的解为：
      // xᵢ = Dᵢ / D
      // D = | a  b |
      //     | c  d | = a*d - b*c

      // 计算 Dₓ（替换第一列）：
      // Dₓ = | e  b |
      //      | f  d | = e*d - b*f

      // 计算 Dᵧ（替换第二列）：
      // Dᵧ = | a  e |
      //      | c  f | = a*f - c*e
      // x₀ = Dₓ / D = (e*d - b*f) / (a*d - b*c)
      // y₀ = Dᵧ / D = (a*f - c*e) / (a*d - b*c)

      let D = a * d - b * c;
      if (Math.abs(D) > 1e-10) {
        let x0 = (e * d - b * f) / (a * d - b * c);
        let y0 = (a * f - c * e) / (a * d - b * c);
        let rSqure = Math.sqrt(
          (dataA.x - x0) * (dataA.x - x0) + (dataA.y - y0) * (dataA.y - y0)
        );
        let center = new THREE.Vector3(x0, y0, 0);

        this.m_arcOptions.center = center.clone();
        this.m_arcOptions.radius = rSqure;
        let Vec2A = new THREE.Vector2(dataA.x, dataA.y);
        Vec2A.sub(new THREE.Vector2(center.x, center.y));
        let Vec2C = new THREE.Vector2(dataC.x, dataC.y);
        Vec2C.sub(new THREE.Vector2(center.x, center.y));
        let startAngle = Vec2A.angle();
        let endAngle = Vec2C.angle();
        let arc = new THREE.ArcCurve(
          this.m_arcOptions.center.x,
          this.m_arcOptions.center.y,
          this.m_arcOptions.radius,
          startAngle,
          endAngle,
          this.m_arcOptions.clockwise
        );
        points = arc.getPoints(50);

        for (let i = 0; i < points.length; i++) {
          if (i > 1) {
            if (i % 2 == 0) {
              points.splice(i, 1, points[i - 1], points[i]);
            }
          }
        }
      } else {
        // 无解或无穷解
      }
    }
    return points;
  }
}

export { DrawOperationArc, DrawOperateLineParams };
